// FTUE anonymous-reveal resume across the Google OIDC round trip.
//
// "Continue with Google" on the pre-claim account step is a full-page
// navigation (/v1/auth/google/start 302s to Google), which destroys the
// React state holding the reveal token and the boat name from the name
// beat. Both are stashed here just before that navigation and restored by
// RevealEntry when the callback lands the browser back on /reveal with a
// session but no #rt= fragment.
//
// Token sensitivity: the reveal token is read-scoped + claim-enabling with
// a short server TTL, and it already arrived exposed in the URL fragment.
// sessionStorage is tab-scoped and dies with the tab, so this is no wider
// an exposure — and it deliberately never touches a server log or redirect.
//
// NOT consume-on-read: a refresh between the OAuth return and the claim
// must still resume. Cleared at terminal states instead (claim success,
// reveal expired) — see AnonymousReveal.tsx.

const KEY = 'nc_reveal_resume';

export interface RevealResume {
  revealToken: string;
  name: string;
}

export function saveRevealResume(resume: RevealResume): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(resume));
  } catch {
    // Storage unavailable (private mode) — the OAuth return will land on
    // the empty /reveal prompt and the user restarts from the boat link.
  }
}

export function loadRevealResume(): RevealResume | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as RevealResume).revealToken === 'string' &&
      (parsed as RevealResume).revealToken.length > 0 &&
      typeof (parsed as RevealResume).name === 'string'
    ) {
      return {
        revealToken: (parsed as RevealResume).revealToken,
        name: (parsed as RevealResume).name,
      };
    }
  } catch {
    // Unparseable — fall through to the clear below.
  }
  // Malformed → clear so it can't wedge every future /reveal load.
  clearRevealResume();
  return null;
}

export function clearRevealResume(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear if storage never worked.
  }
}
